import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';
import PatientService from '../../services/patientService';
import Button from '../common/Button';
import PatientsView from './PatientsView';
import { formatDateEs } from '../../utils';
import './Patients.css';

const patientListFields = ['id', 'firstName', 'lastName', 'email', 'cellPhone', 'dateOfBirth'];

const Patients = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [deletedPatients, setDeletedPatients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active'); // 'active' or 'deleted'

  const [message, setMessage] = useState({ type: '', text: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredPatients, setFilteredPatients] = useState([]);

  const loadPatients = useCallback(async () => {
    try {
      const token = auth.user?.access_token;
      const result = await PatientService.getAllPatientsAllPages(token, 500, patientListFields);
      const patientsList = result.patients || [];
      setPatients(patientsList);
      if (activeTab === 'active') {
        setFilteredPatients(sortPatientsByName(patientsList));
      }
    } catch (error) {
      const message = error?.message.includes('402') ? 'La suscripción del usuario está inactiva. Por favor, renueva tu suscripción para acceder a este recurso.' : 'Error al cargar los pacientes';
      setMessage({
        type: 'error',
        text: message
      });
    } finally {
      setIsLoading(false);
    }
  }, [auth.user?.access_token, activeTab]);

  const loadDeletedPatients = useCallback(async () => {
    try {
      const token = auth.user?.access_token;
      const result = await PatientService.getDeletedPatientsAllPages(token, 500, patientListFields);
      const patientsList = result.patients || [];
      setDeletedPatients(patientsList);
      if (activeTab === 'deleted') {
        setFilteredPatients(sortPatientsByName(patientsList));
      }
    } catch (error) {
      const message = error?.message.includes('402') ? 'La suscripción del usuario está inactiva. Por favor, renueva tu suscripción para acceder a este recurso.' : 'Error al cargar los pacientes archivados';
      setMessage({
        type: 'error',
        text: message
      });
    } finally {
      setIsLoading(false);
    }
  }, [auth.user?.access_token, activeTab]);

  // Load patients on component mount and when tab changes
  useEffect(() => {
    setIsLoading(true);
    setSearchQuery('');
    if (activeTab === 'active') {
      loadPatients();
    } else {
      loadDeletedPatients();
    }
  }, [activeTab, loadPatients, loadDeletedPatients]);

  const sortPatientsByName = (patientsList) => {
    return [...patientsList].sort((a, b) => {
      const nameA = `${a.firstName || ''} ${a.lastName || ''}`.trim().toLowerCase();
      const nameB = `${b.firstName || ''} ${b.lastName || ''}`.trim().toLowerCase();
      return nameA.localeCompare(nameB);
    });
  };

  const handleSearch = useCallback((query) => {
    const trimmedQuery = query.trim().toLowerCase();
    
    const currentPatients = activeTab === 'active' ? patients : deletedPatients;
    
    if (!trimmedQuery) {
      setFilteredPatients(sortPatientsByName(currentPatients));
      return;
    }

    const filtered = currentPatients.filter(patient => {
      const fullName = `${patient.firstName || ''} ${patient.lastName || ''}`.toLowerCase();
      const email = (patient.email || '').toLowerCase();
      const phone = (patient.cellPhone || patient.phoneNumber || '').toLowerCase();
      
      return (
        fullName.includes(trimmedQuery) ||
        email.includes(trimmedQuery) ||
        phone.includes(trimmedQuery)
      );
    });

    setFilteredPatients(sortPatientsByName(filtered));
  }, [patients, deletedPatients, activeTab]);

  // Filter patients when search query changes with debouncing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handleSearch(searchQuery);
    }, 300); // 300ms delay

    return () => clearTimeout(timeoutId);
  }, [searchQuery, handleSearch]);

  const handleCreatePatient = () => {
    navigate('/patients/new');
  };

  const handleViewPatient = (patient) => {
    // Only allow navigation if it's an active patient
    if (activeTab === 'active') {
      navigate(`/patients/${patient.id}`);
    }
  };

  const handleRestorePatient = async (patient) => {
    if (window.confirm(`¿Estás seguro de que deseas restaurar a ${patient.firstName} ${patient.lastName}?`)) {
      try {
        const token = auth.user?.access_token;
        await PatientService.restorePatient(patient.id, token);
        
        setMessage({
          type: 'success',
          text: `Paciente ${patient.firstName} ${patient.lastName} restaurado exitosamente`
        });
        
        // Refresh both lists
        await loadDeletedPatients();
        await loadPatients();
        
        // Clear message after 3 seconds
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } catch (error) {
        setMessage({
          type: 'error',
          text: 'Error al restaurar el paciente'
        });
      }
    }
  };

  return (
    <div className="patients-container">
      <div className="patients-header">
        <div className="header-content">
          <h1>Gestión de Pacientes</h1>
          <p>Administra la información de tus pacientes</p>
        </div>
        <Button 
          variant="primary" 
          size="lg" 
          icon="fas fa-plus"
          onClick={handleCreatePatient}
        >
          Nuevo Paciente
        </Button>
      </div>

      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Tabs */}
      <div className="patients-tabs">
        <button 
          className={`tab ${activeTab === 'active' ? 'active' : ''}`}
          onClick={() => setActiveTab('active')}
        >
          <i className="fas fa-users"></i>
          Pacientes Activos
          <span className="tab-count">{patients.length}</span>
        </button>
        <button 
          className={`tab ${activeTab === 'deleted' ? 'active' : ''}`}
          onClick={() => setActiveTab('deleted')}
        >
          <i className="fas fa-archive"></i>
          Pacientes Archivados
          {(deletedPatients.length > 0 || activeTab === 'deleted') && (
            <span className="tab-count">{deletedPatients.length}</span>
          )}
        </button>
      </div>

      {/* Search Bar */}
      <div className="search-section">
                  <div className={`search-bar ${searchQuery ? 'searching' : ''}`}>
            <input
              type="text"
              placeholder="Buscar pacientes por nombre, email o teléfono..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery ? (
              <Button 
                variant="outline" 
                icon="fas fa-times" 
                onClick={() => {
                  setSearchQuery('');
                  setFilteredPatients(activeTab === 'active' ? patients : deletedPatients);
                }}
                title="Limpiar búsqueda"
              />
            ) : (
              <></>
            )}
          </div>
      </div>

      {/* Patients List */}
      <div className="patients-content">
        {isLoading ? (
          <div className="patients-loading">
            <div className="loading-spinner"></div>
          </div>
        ) : filteredPatients.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-search"></i>
            <h3>No se encontraron resultados</h3>
            <Button 
              variant="outline" 
              onClick={() => {
                setSearchQuery('');
                setFilteredPatients(activeTab === 'active' ? patients : deletedPatients);
              }}
            >
              Ver todos los pacientes
            </Button>
          </div>
        ) : (
          <PatientsView
            patients={filteredPatients}
            onView={activeTab === 'active' ? handleViewPatient : undefined}
            onRestore={activeTab === 'deleted' ? handleRestorePatient : undefined}
            formatDate={formatDateEs}
            readOnly={activeTab === 'deleted'}
          />
        )}
      </div>
    </div>
  );
};

export default Patients;